import type { ReactNode } from 'react';
import { ArrowRightIcon, CaretRightIcon, InfoIcon } from '@phosphor-icons/react';
import { Avatar, AvatarBadge, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import Link from '@/components/ui/Link';
import { MoneyValue } from '@/components/ui/MoneyValue';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { memberPhoto, participantName, transferHint } from '@/lib/spaces/settlement';
import { cn } from '@/lib/utils';
import type { SpaceDetail } from '@/types/spaces';
import type { SpaceTransfer } from '@/types/space-settlements';

export type SettlementCardAction = { label: string; href: string } | { label: string; onClick: () => void };

// The eyebrow stays text-secondary: tertiary is 4.17:1 on the card, under AA for text this small.
export function SettlementCard({ title, info, action, className, children }: Readonly<{ title: string; info?: ReactNode; action?: SettlementCardAction | null; className?: string; children: ReactNode }>) {
  return <section aria-label={title} className={cn('min-w-0 space-y-4 rounded-3xl border border-border-subtle bg-surface p-4 shadow-sm sm:p-5', className)}>
    <header className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-1.5"><h2 className="min-w-0 break-words font-mono text-2xs font-semibold uppercase tracking-widest text-text-secondary">{title}</h2>{info}</div>
      <CardAction action={action} />
    </header>{children}
  </section>;
}

const ACTION = 'flex shrink-0 cursor-pointer items-center gap-0.5 text-xs font-medium text-text-secondary transition-colors hover:text-text-primary';

function CardAction({ action }: Readonly<{ action?: SettlementCardAction | null }>) {
  if (!action) return null;
  const content = <>{action.label}<CaretRightIcon aria-hidden weight="bold" className="size-3" /></>;
  if ('href' in action) return <Link href={action.href} className={ACTION}>{content}</Link>;
  return <button type="button" onClick={action.onClick} className={ACTION}>{content}</button>;
}

// Explanations live behind an ⓘ, never as a paragraph in a card's body. `tone` matches the icon to its surface (the
// green hero or a card).
export function SpaceInfoButton({ label, tone = 'text-text-secondary hover:text-text-primary', children }: Readonly<{ label: string; tone?: string; children: ReactNode }>) {
  return <Popover>
    <PopoverTrigger asChild>
      <button type="button" aria-label={label} className={cn('-m-1.5 shrink-0 cursor-pointer rounded-full p-1.5 transition-colors', tone)}><InfoIcon aria-hidden className="size-3.5" /></button>
    </PopoverTrigger>
    <PopoverContent side="bottom" align="start" collisionPadding={12} className="w-72 space-y-2 rounded-xl p-4 text-xs leading-relaxed text-text-secondary">{children}</PopoverContent>
  </Popover>;
}

export function SettlementMoney({ cents, className = '', signed = false }: Readonly<{ cents: number; className?: string; signed?: boolean }>) {
  const format = useFormatCurrency();
  const text = `${signed && cents > 0 ? '+' : ''}${format(cents / 100)}`;
  return <span className={`whitespace-nowrap font-mono tabular-nums ${className}`}><MoneyValue text={text} centsClassName="text-xs text-text-secondary opacity-100" /></span>;
}

// A member's thumbnail: the photo they agreed to show the space on joining, or their initials. Decorative —
// the name is always written next to it — so the image carries no alt text and the whole thing is hidden
// from assistive tech. The initials stay underneath until the picture has loaded (and if it never does), and they
// are drawn by CSS from a data attribute so they never join the row's text (screen readers, tests, copy-paste).
export function SettlementAvatar({ name, photoUrl, className, badgeClassName }: Readonly<{ name: string; photoUrl?: string | null; className?: string; badgeClassName?: string }>) {
  const initials = name.split(' · ')[0].split(/\s+/).slice(0, 2).map((part) => part[0]).join('');
  // The initials take the size the caller gives the avatar (`text-3xs` on a 16px one): the root carries the font size,
  // defaulting to text-xs, and the fallback inherits it instead of fixing its own.
  return <Avatar aria-hidden data-testid="member-avatar" className={cn('border border-border-subtle bg-surface text-xs', badgeClassName && 'overflow-visible', className)}>
    <span className="flex size-full overflow-hidden rounded-full">
      {photoUrl ? <AvatarImage src={photoUrl} alt="" className="object-cover" /> : null}
      <AvatarFallback data-initials={initials} className="bg-surface text-[length:inherit] font-semibold text-text-secondary before:content-[attr(data-initials)]" />
    </span>
    {badgeClassName ? <AvatarBadge className={cn('size-2.5 ring-1', badgeClassName)} /> : null}
  </Avatar>;
}

// The whole roster at a glance: overlapping thumbnails in join order, capped so a header stays a header.
export function SettlementAvatarGroup({ space, max = 5, className }: Readonly<{ space: SpaceDetail; max?: number; className?: string }>) {
  const shown = space.members.slice(0, max);
  const rest = space.members.length - shown.length;
  const renderMember = (member: SpaceDetail['members'][number]) => <SettlementAvatar key={member.userId} name={participantName(space, member.userId)} photoUrl={member.photoUrl} className="size-6 text-2xs" />;
  return <AvatarGroup aria-hidden className={cn('shrink-0', className)}>{shown.map(renderMember)}{rest > 0 ? <AvatarGroupCount className="size-6 text-2xs">+{rest}</AvatarGroupCount> : null}</AvatarGroup>;
}

export function SettlementTransferRow({ transfer, space, userId, compact = false }: Readonly<{
  transfer: SpaceTransfer; space: SpaceDetail; userId?: string | null;
  /** One line for a narrow card: the names wrap before the amount leaves the line, and the hint gives way to the card's framing. */
  compact?: boolean;
}>) {
  const from = participantName(space, transfer.fromUserId);
  const to = participantName(space, transfer.toUserId);
  if (compact) return <div className="flex min-w-0 items-center gap-2.5">
    <div aria-hidden className="flex shrink-0 items-center gap-0.5"><SettlementAvatar name={from} photoUrl={memberPhoto(space, transfer.fromUserId)} className="size-6 text-2xs" /><ArrowRightIcon className="size-3 text-text-secondary" /><SettlementAvatar name={to} photoUrl={memberPhoto(space, transfer.toUserId)} className="size-6 text-2xs" /></div>
    <p className="min-w-0 flex-1 break-words text-sm font-medium text-text-primary">{from} paga {to}<span className="sr-only"> · {transferHint(transfer, userId)}</span></p>
    <SettlementMoney cents={transfer.amountCents} className="shrink-0 text-sm font-semibold text-text-primary" />
  </div>;
  return <div className="flex min-w-0 flex-wrap items-center gap-3">
    <div className="flex min-w-0 flex-1 basis-48 items-center gap-3">
      <div className="flex shrink-0 items-center gap-1"><SettlementAvatar name={from} photoUrl={memberPhoto(space, transfer.fromUserId)} /><ArrowRightIcon aria-hidden className="size-3 text-text-secondary" /><SettlementAvatar name={to} photoUrl={memberPhoto(space, transfer.toUserId)} /></div>
      <div className="min-w-0"><p className="break-words text-sm font-medium text-text-primary">{from} paga {to}</p><p className="text-xs text-text-secondary">{transferHint(transfer, userId)}</p></div>
    </div>
    <SettlementMoney cents={transfer.amountCents} className="ml-auto text-lg font-semibold text-text-primary" />
  </div>;
}

export function SettlementFailure({ message, retry }: Readonly<{ message: string; retry: () => unknown }>) {
  const handleRetry = () => { void Promise.resolve(retry()).catch(() => undefined); };
  return <div role="alert" className="space-y-3 text-sm text-text-secondary"><p>{message}</p><Button variant="outline" size="sm" onClick={handleRetry}>Tentar novamente</Button></div>;
}

export function SettlementPagination({ offset, total, limit, onPage }: Readonly<{ offset: number; total: number; limit: number; onPage: (offset: number) => void }>) {
  if (total <= limit && offset === 0) return null;
  const previous = () => onPage(Math.max(0, offset - limit));
  const next = () => onPage(offset + limit);
  return <div className="flex flex-wrap items-center justify-between gap-2 pt-2 text-xs text-text-secondary">
    <span>{Math.min(offset + 1, total)}–{Math.min(offset + limit, total)} de {total}</span>
    <div className="flex gap-1"><Button variant="ghost" size="sm" disabled={!offset} onClick={previous}>Anteriores</Button><Button variant="ghost" size="sm" disabled={offset + limit >= total} onClick={next}>Próximos</Button></div>
  </div>;
}

